import React, { useEffect, useRef, useState } from "react";

const DEFAULT_PLACEHOLDER = "- Choose value -";

// Drop-in replacement for a native <select> that renders HTML in the trigger
// and option list. Used by QuestionsAnswersArray so option labels containing
// <sup>/<sub> from the Quill rich-text inputs display as actual super/subscript
// rather than the raw tags that <option> elements would expose.
const RichHtmlSelect = ({
  value = "",
  onChange,
  onBlur,
  options = [],
  // name,
  className = "",
  placeholder = DEFAULT_PLACEHOLDER,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  // react-reactive-form's onChange treats the argument as a raw value unless it
  // looks like a DOM event (has stopPropagation + preventDefault). Passing the
  // value directly is the simplest way to update the form control.
  const selectValue = newValue => {
    onChange?.(newValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(o => String(o.value) === String(value));

  return (
    <div
      ref={wrapperRef}
      className={`rich-html-select ${isOpen ? "is-open" : ""} ${className}`}>
      <button
        type="button"
        className="rich-html-select__control input-select"
        disabled={disabled}
        onClick={() => setIsOpen(prev => !prev)}>
        {selectedOption ? (
          <span
            className="rich-html-select__value"
            dangerouslySetInnerHTML={{ __html: selectedOption.title }}
          />
        ) : (
          <span className="rich-html-select__placeholder">{placeholder}</span>
        )}
      </button>
      {isOpen && (
        <ul className="rich-html-select__menu" role="listbox">
          <li
            className="rich-html-select__option rich-html-select__option--placeholder"
            role="option"
            aria-selected={!selectedOption}
            onClick={() => selectValue("")}>
            {placeholder}
          </li>
          {options.map((opt, i) => (
            <li
              key={`${opt.value}-${i}`}
              role="option"
              aria-selected={String(opt.value) === String(value)}
              className={`rich-html-select__option ${
                String(opt.value) === String(value) ? "is-selected" : ""
              }`}
              onClick={() => selectValue(opt.value)}
              dangerouslySetInnerHTML={{ __html: opt.title }}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default RichHtmlSelect;
